import {
  lazy,
  Suspense,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import styled from "@emotion/styled";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { LinearProgress } from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  type GridColDef,
  type GridRowParams,
} from "@mui/x-data-grid";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmberOutlined";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import {
  Box,
  Button,
  IconButton,
  SubmitButton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useBreakpoint,
  useTheme,
} from "@avenue-8/sidekick-ui";
import {
  listSuggestionsQueryKey,
  useCreateContactsBatch,
  type BatchCreateContactResultDto,
} from "@shared/generated/crm";
import {
  getConsumptionQueryKey,
  useGetConsumption,
} from "@shared/generated/radar/queries/contactsController/useGetConsumption";
import { getConsumptionQueryResponseSchema } from "@shared/generated/radar/zod/contactsController/getConsumptionSchema";
import { useHasModule } from "@shared/access-control/useHasModule";
import { sidekickModuleEnum } from "@shared/access-control/sidekick-modules";
import { toCreateContactBody } from "@features/Contacts/components/NewContactDialog/new-contact-form-helpers";
import { appConfig } from "@shared/utils/appConfig";
import { toast } from "@shared/services/toast";
import {
  BATCH_CHUNK_SIZE,
  buildImportBatches,
  type ParsedCsvRow,
} from "./csv-import-helpers";
import { MonitorAllOption, type MonitoringSlots } from "./MonitorAllOption";
import { isFetchClientError } from "@shared/apis/fetch-clients/fetch-client-error";
import {
  toCsvContactRows,
  validateCsvContactRow,
  type CsvContactFieldValues,
  type CsvContactRow,
} from "./csv-row-helpers";

const DryRunToggle = lazy(() =>
  import("./DryRunToggle").then((module) => ({
    default: module.DryRunToggle,
  })),
);

type ImportContactsCsvPreviewStepProps = {
  parsedRows: ParsedCsvRow[];
  onCancel: () => void;
  onImported?: () => void;
};

const PREVIEW_FIELDS = [
  { id: "firstName", label: "First Name", width: 140 },
  { id: "lastName", label: "Last Name", width: 140 },
  { id: "fullName", label: "Full Name", width: 160 },
  { id: "preferredName", label: "Preferred Name", width: 160 },
  { id: "email", label: "Email", width: 220 },
  { id: "phone", label: "Phone", width: 140 },
  { id: "company", label: "Company", width: 160 },
  { id: "jobTitle", label: "Job Title", width: 160 },
  { id: "timezone", label: "Timezone", width: 140 },
  { id: "address", label: "Address", width: 200 },
  { id: "city", label: "City", width: 140 },
  { id: "state", label: "State", width: 100 },
  { id: "postalCode", label: "Postal Code", width: 120 },
  { id: "instagramHandle", label: "Instagram", width: 140 },
  { id: "linkedinHandle", label: "LinkedIn", width: 140 },
  { id: "facebookHandle", label: "Facebook", width: 140 },
  { id: "links", label: "Links", width: 220 },
] as const;

const ALWAYS_VISIBLE_FIELD_IDS = new Set([
  "firstName",
  "lastName",
  "email",
  "phone",
]);

type ImportResult = {
  successCount: number;
  monitoredCount: number;
  unmonitoredForLimitCount: number;
  failedCount: number;
  dryRun: boolean;
  stoppedEarly: boolean;
};

type ImportProgress = {
  completedChunks: number;
  totalChunks: number;
};

const PREVIEW_TAB_ID = "csv-import-preview-tab";
const PREVIEW_PANEL_ID = "csv-import-preview-panel";
const RESULTS_TAB_ID = "csv-import-results-tab";
const RESULTS_PANEL_ID = "csv-import-results-panel";

const TabPanel = ({
  active,
  id,
  labelledBy,
  children,
}: {
  active: boolean;
  id: string;
  labelledBy: string;
  children: ReactNode;
}) =>
  active ? (
    <TabPanelContainer role="tabpanel" id={id} aria-labelledby={labelledBy}>
      {children}
    </TabPanelContainer>
  ) : null;

export const ImportContactsCsvPreviewStep = ({
  parsedRows,
  onCancel,
  onImported,
}: ImportContactsCsvPreviewStepProps) => {
  const [rows, setRows] = useState<CsvContactRow[]>(() =>
    toCsvContactRows(parsedRows),
  );
  const [dryRun, setDryRun] = useState(false);
  const [monitorAll, setMonitorAll] = useState(false);
  const hasRadarModule = useHasModule(sidekickModuleEnum.RADAR);
  const queryClient = useQueryClient();
  const consumptionQuery = useGetConsumption(undefined, {
    query: { enabled: hasRadarModule },
  });
  const consumption = getConsumptionQueryResponseSchema.safeParse(
    consumptionQuery.data,
  );
  const monitoringSlots: MonitoringSlots = consumption.success
    ? { status: "ready", remaining: consumption.data.remaining }
    : consumptionQuery.isLoading
      ? { status: "loading" }
      : isFetchClientError(consumptionQuery.error) &&
          consumptionQuery.error.status === 403
        ? { status: "not-set-up" }
        : { status: "unavailable" };
  const remainingMonitoringSlots =
    monitoringSlots.status === "ready" ? monitoringSlots.remaining : 0;
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importProgress, setImportProgress] = useState<ImportProgress | null>(
    null,
  );
  const [activeTab, setActiveTab] = useState<"preview" | "results">("preview");
  const isMobile = useBreakpoint("mobile");
  const actionButtonSize = isMobile ? "small" : "medium";
  const createContactsBatchMutation = useCreateContactsBatch();
  const theme = useTheme();
  const navigate = useNavigate();

  const isDone = rows.length === 0;
  const isImporting = importProgress !== null;
  const hasPersistedContacts = Boolean(
    importResult && !importResult.dryRun && importResult.successCount > 0,
  );

  const visibleFields = useMemo(
    () =>
      PREVIEW_FIELDS.filter(
        (field) =>
          ALWAYS_VISIBLE_FIELD_IDS.has(field.id) ||
          rows.some((row) => row.values[field.id]),
      ),
    [rows],
  );

  const validationsByRowId = useMemo(
    () =>
      Object.fromEntries(
        rows.map((row) => [row.id, validateCsvContactRow(row.values)]),
      ),
    [rows],
  );
  const validCount = rows.filter(
    (row) => validationsByRowId[row.id].valid,
  ).length;

  const serverErrorsByRowId = useMemo(
    () => Object.fromEntries(rows.map((row) => [row.id, row.serverError])),
    [rows],
  );

  const rowNumberByRowId = useMemo(
    () => Object.fromEntries(rows.map((row, index) => [row.id, index + 1])),
    [rows],
  );

  const errorMessagesByRowId = useMemo(
    () =>
      Object.fromEntries(
        rows.map((row) => {
          const serverError = serverErrorsByRowId[row.id];
          const validation = validationsByRowId[row.id];
          const errorMessages = serverError
            ? { server: serverError }
            : !validation.valid
              ? validation.errorsByField
              : null;
          return [row.id, errorMessages];
        }),
      ),
    [rows, serverErrorsByRowId, validationsByRowId],
  );

  const removeRow = (rowId: string) => {
    setRows((prev) => prev.filter((row) => row.id !== rowId));
  };

  const processRowUpdate = (newRow: { id: string } & CsvContactFieldValues) => {
    const { id, ...values } = newRow;
    setRows((prev) =>
      prev.map((row) =>
        row.id === id ? { ...row, values, serverError: undefined } : row,
      ),
    );
    return newRow;
  };

  const gridRows = useMemo(
    () => rows.map((row) => ({ id: row.id, ...row.values })),
    [rows],
  );

  const gridContainerRef = useRef<HTMLDivElement>(null);
  const scrollGrid = (direction: "left" | "right") => {
    const scroller = gridContainerRef.current?.querySelector<HTMLDivElement>(
      ".MuiDataGrid-virtualScroller",
    );
    if (!scroller) return;
    const amount = scroller.clientWidth * 0.8;
    scroller.scrollBy({
      left: direction === "left" ? -amount : amount,
      behavior: "smooth",
    });
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "__rowNumber",
        headerName: "#",
        width: 40,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        cellClassName: (params) =>
          errorMessagesByRowId[params.row.id] ? "csv-row-error-cell" : "",
        renderCell: (params) => {
          const rowNumber = rowNumberByRowId[params.row.id];
          const errorMessages = errorMessagesByRowId[params.row.id];

          if (!errorMessages) {
            return (
              <StatusCell>
                <Typography variant="caption" color="text.secondary">
                  {rowNumber}
                </Typography>
              </StatusCell>
            );
          }
          return (
            <Tooltip
              slotProps={{
                tooltip: {
                  sx: { backgroundColor: "transparent", padding: 0 },
                },
              }}
              title={
                <ErrorTooltipBody>
                  {Object.entries(errorMessages).map(([fieldId, message]) => (
                    <Typography key={fieldId} variant="caption" color="error">
                      {message}
                    </Typography>
                  ))}
                </ErrorTooltipBody>
              }
            >
              <StatusCell>
                <Typography variant="caption" color="error">
                  {rowNumber}
                </Typography>
              </StatusCell>
            </Tooltip>
          );
        },
      },
      ...visibleFields.map(
        (field): GridColDef => ({
          field: field.id,
          headerName: field.label,
          width: field.width,
          editable: true,
        }),
      ),
      {
        field: "__actions",
        type: "actions",
        headerName: "",
        width: 56,
        getActions: (params: GridRowParams) => [
          <GridActionsCellItem
            key="delete"
            icon={<DeleteOutlineIcon fontSize="small" />}
            label="Remove row"
            onClick={() => removeRow(params.id as string)}
            disabled={isImporting}
          />,
        ],
      },
    ],
    [visibleFields, errorMessagesByRowId, rowNumberByRowId, isImporting],
  );

  const handleImport = async (event: React.FormEvent) => {
    event.preventDefault();

    const validRows = rows.filter((row) => validationsByRowId[row.id].valid);
    const contacts = validRows.map((row) => toCreateContactBody(row.values));
    const isMonitorRequested =
      hasRadarModule && monitorAll && remainingMonitoringSlots > 0;
    const batches = buildImportBatches({
      contactsCount: contacts.length,
      monitorLimit: isMonitorRequested ? remainingMonitoringSlots : 0,
    });
    const totalChunks = batches.length;

    const resultByRowId = new Map<string, BatchCreateContactResultDto>();
    const unmonitoredForLimitRowIds = new Set<string>();
    let stoppedEarly = false;

    setImportProgress({ completedChunks: 0, totalChunks });

    for (const [chunk, batch] of batches.entries()) {
      const chunkRows = validRows.slice(batch.start, batch.end);
      const chunkContacts = contacts.slice(batch.start, batch.end);

      try {
        const response = await createContactsBatchMutation.mutateAsync({
          data: {
            contacts: chunkContacts,
            dryRun,
            monitor: batch.monitor,
          },
        });

        const seenIndexes = new Set<number>();
        const isWellFormed =
          response.results.length === chunkContacts.length &&
          response.results.every((result) => {
            const isInRange =
              result.index >= 0 && result.index < chunkContacts.length;
            const isUnique = isInRange && !seenIndexes.has(result.index);
            if (isUnique) seenIndexes.add(result.index);
            return isUnique;
          });
        if (!isWellFormed) throw new Error("Malformed batch response");

        response.results.forEach((result) => {
          const rowId = chunkRows[result.index].id;
          resultByRowId.set(rowId, result);
          if (isMonitorRequested && !batch.monitor && result.success) {
            unmonitoredForLimitRowIds.add(rowId);
          }
        });

        setImportProgress({ completedChunks: chunk + 1, totalChunks });
      } catch {
        stoppedEarly = true;
        break;
      }
    }

    setImportProgress(null);

    if (stoppedEarly && resultByRowId.size === 0) {
      toast.error("Something went wrong importing contacts. Please try again.");
      return;
    }

    const failedCount = [...resultByRowId.values()].filter(
      (result) => !result.success,
    ).length;
    const successCount = resultByRowId.size - failedCount;
    const monitoredCount = [...resultByRowId.values()].filter(
      (result) => result.success && result.contact?.isMonitoredSince,
    ).length;

    // A row only leaves the preview once it's actually persisted. Rows
    // never submitted (client-invalid, or never reached because an earlier
    // chunk failed) and dry-run "successes" (nothing is saved on a dry run)
    // both stay so they aren't lost from the CSV.
    setRows((prev) =>
      prev.reduce<CsvContactRow[]>((next, row) => {
        const result = resultByRowId.get(row.id);
        if (!result) {
          next.push(row);
        } else if (!result.success) {
          next.push({
            ...row,
            serverError: result.error ?? "Failed to create contact",
          });
        } else if (dryRun) {
          next.push({ ...row, serverError: undefined });
        }
        return next;
      }, []),
    );

    if (!dryRun && successCount > 0) onImported?.();
    if (monitoredCount > 0) {
      void queryClient.invalidateQueries({
        queryKey: getConsumptionQueryKey(),
      });
      void queryClient.invalidateQueries({
        queryKey: listSuggestionsQueryKey(),
      });
    }

    setImportResult({
      successCount,
      monitoredCount,
      unmonitoredForLimitCount: dryRun ? 0 : unmonitoredForLimitRowIds.size,
      failedCount,
      dryRun,
      stoppedEarly,
    });
    setActiveTab("results");
  };

  return (
    <Form onSubmit={handleImport}>
      <TabsWrapper>
        <Tabs
          value={activeTab}
          onChange={(_, value: "preview" | "results") => setActiveTab(value)}
        >
          <Tab
            label="Preview"
            value="preview"
            disabled={isDone}
            id={PREVIEW_TAB_ID}
            aria-controls={PREVIEW_PANEL_ID}
          />
          <Tab
            label="Results"
            value="results"
            disabled={!importResult}
            id={RESULTS_TAB_ID}
            aria-controls={RESULTS_PANEL_ID}
          />
        </Tabs>
      </TabsWrapper>

      <TabPanel
        active={activeTab === "preview"}
        id={PREVIEW_PANEL_ID}
        labelledBy={PREVIEW_TAB_ID}
      >
        <PreviewPanel>
          {hasRadarModule && !isDone && (
            <MonitorAllOption
              checked={monitorAll}
              onChange={setMonitorAll}
              disabled={isImporting}
              contactsCount={validCount}
              slots={monitoringSlots}
            />
          )}
          <SummaryRow>
            <Typography>
              {validCount} of {rows.length} contacts ready to import
            </Typography>
            <ScrollButtons>
              {appConfig.APP_ENV !== "production" && (
                <Suspense fallback={null}>
                  <DryRunToggle checked={dryRun} onChange={setDryRun} />
                </Suspense>
              )}
              <Tooltip title="Scroll left">
                <IconButton
                  size="small"
                  onClick={() => scrollGrid("left")}
                  aria-label="Scroll table left"
                  disabled={isImporting}
                >
                  <KeyboardDoubleArrowLeftIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Scroll right">
                <IconButton
                  size="small"
                  onClick={() => scrollGrid("right")}
                  aria-label="Scroll table right"
                  disabled={isImporting}
                >
                  <KeyboardDoubleArrowRightIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </ScrollButtons>
          </SummaryRow>
          <GridContainer ref={gridContainerRef}>
            <DataGrid
              rows={gridRows}
              columns={columns}
              density="compact"
              disableRowSelectionOnClick
              disableColumnMenu
              isCellEditable={() => !importProgress}
              pageSizeOptions={[BATCH_CHUNK_SIZE]}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: BATCH_CHUNK_SIZE, page: 0 },
                },
              }}
              processRowUpdate={processRowUpdate}
              onProcessRowUpdateError={() => {}}
              sx={{
                height: "100%",
                pointerEvents: isImporting ? "none" : "auto",
                "& .MuiDataGrid-scrollbar": {
                  display: isImporting ? "none" : undefined,
                },
                "& .MuiDataGrid-editInputCell": {
                  borderRadius: 0,
                },
                "& .csv-row-error-cell": {
                  border: `1px solid ${theme.colors.error}`,
                  borderRadius: 0,
                },
              }}
            />
            {importProgress && importProgress.totalChunks > 1 && (
              <ImportingOverlay>
                <ProgressWrapper>
                  <ProgressLabelRow>
                    <Typography sx={{ fontWeight: 600 }}>
                      Importing contacts...
                    </Typography>
                    <Typography sx={{ fontWeight: 600 }}>
                      {Math.round(
                        (importProgress.completedChunks /
                          importProgress.totalChunks) *
                          100,
                      )}
                      %
                    </Typography>
                  </ProgressLabelRow>
                  <ContactsLinearProgress
                    variant="determinate"
                    value={
                      (importProgress.completedChunks /
                        importProgress.totalChunks) *
                      100
                    }
                  />
                  <Typography variant="body2">
                    Please keep this browser window open until the upload
                    finishes.
                  </Typography>
                </ProgressWrapper>
              </ImportingOverlay>
            )}
          </GridContainer>
        </PreviewPanel>
      </TabPanel>

      <TabPanel
        active={activeTab === "results"}
        id={RESULTS_PANEL_ID}
        labelledBy={RESULTS_TAB_ID}
      >
        {importResult && (
          <ResultContainer>
            {importResult.successCount > 0 && (
              <ResultBlock>
                <CheckCircleIcon color="success" sx={{ fontSize: "2.5rem" }} />
                <Typography variant="subtitle1">
                  {importResult.successCount} contacts imported successfully
                </Typography>
                {importResult.dryRun ? (
                  <Typography variant="caption" color="text.secondary">
                    This was a dry run. Nothing was saved.
                  </Typography>
                ) : (
                  <Typography color="text.secondary">
                    They&apos;re now available in your Contacts list.
                    {importResult.monitoredCount > 0 &&
                      ` ${importResult.monitoredCount} of them will be monitored by Radar.`}
                    {importResult.unmonitoredForLimitCount > 0 &&
                      ` The other ${importResult.unmonitoredForLimitCount} ${importResult.unmonitoredForLimitCount === 1 ? "was" : "were"} imported without monitoring because you ran out of monitoring slots.`}
                  </Typography>
                )}
              </ResultBlock>
            )}

            {importResult.failedCount > 0 && (
              <ResultBlock>
                {importResult.successCount > 0 ? (
                  <WarningAmberIcon
                    color="warning"
                    sx={{ fontSize: "2.5rem" }}
                  />
                ) : (
                  <ErrorOutlineIcon color="error" sx={{ fontSize: "2.5rem" }} />
                )}
                <Typography variant="subtitle1">
                  {importResult.failedCount} contacts couldn&apos;t be imported
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {importResult.successCount > 0
                    ? importResult.dryRun
                      ? "Fix them on the Preview tab, then run the dry run again."
                      : "Fix them on the Preview tab and try again. The contacts that succeeded are already saved."
                    : "Fix the highlighted rows on the Preview tab and try again."}
                </Typography>
              </ResultBlock>
            )}

            {importResult.stoppedEarly && (
              <ResultBlock>
                <WarningAmberIcon color="warning" sx={{ fontSize: "2.5rem" }} />
                <Typography variant="subtitle1">
                  The import stopped early
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Something went wrong partway through. The remaining contacts
                  are still on the Preview tab, ready to try again.
                </Typography>
              </ResultBlock>
            )}
          </ResultContainer>
        )}
      </TabPanel>

      <Actions>
        {isDone ? (
          <Button
            variant="contained"
            size={actionButtonSize}
            onClick={() => {
              onCancel();
              if (hasPersistedContacts) navigate("/contacts");
            }}
          >
            {hasPersistedContacts ? "View Contacts" : "Close"}
          </Button>
        ) : (
          <>
            <Button
              variant="text"
              size={actionButtonSize}
              onClick={onCancel}
              disabled={isImporting}
            >
              Close
            </Button>
            <SubmitButton
              variant="contained"
              size={actionButtonSize}
              isLoading={isImporting}
              loadingText={dryRun ? "Running..." : "Importing..."}
              disabled={validCount === 0 || isImporting}
            >
              {dryRun
                ? `Dry run ${validCount} contacts`
                : `Import ${validCount} contacts`}
            </SubmitButton>
          </>
        )}
      </Actions>
    </Form>
  );
};

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  min-height: 0;
`;

const TabsWrapper = styled.div`
  border-bottom: 1px solid ${(p) => p.theme.colors.border2};
`;

const TabPanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const PreviewPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  min-height: 0;
  padding-top: 0.5rem;
`;

const ResultContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4rem;
  padding: 3rem 1rem;
  text-align: center;
  min-height: 480px;
`;

const ResultBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.375rem;
`;

const ImportingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 0 2rem;
  background-color: ${(p) => p.theme.colors.background}cc;

  ${(p) => p.theme.media.mobileDown} {
    padding: 0 1rem;
  }
`;

const ProgressWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  width: 60%;

  ${(p) => p.theme.media.mobileDown} {
    width: 100%;
  }
`;

const ProgressLabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const ContactsLinearProgress = styled(LinearProgress)`
  height: 0.5rem;
  border-radius: 0.25rem;

  .MuiLinearProgress-bar {
    background-color: ${(p) => p.theme.text.colors.brand};
    border-radius: 0.25rem;
  }
`;

const SummaryRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const ScrollButtons = styled.div`
  display: flex;
  gap: 0.25rem;
`;

const GridContainer = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;
  flex: 1;
  min-height: 20rem;

  ${(p) => p.theme.media.mobileDown} {
    min-height: 16rem;
  }
`;

const StatusCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`;

const ErrorTooltipBody = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.75rem;
  border-radius: 0.375rem;
  background-color: ${(p) => p.theme.colors.background};
  border: 1px solid ${(p) => p.theme.colors.border2};
  box-shadow: ${(p) => p.theme.shadows.primary};

  & .MuiTypography-root {
    font-weight: 600;
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  flex-shrink: 0;
  padding-top: 1.25rem;
  position: sticky;
  bottom: 0;
  background-color: ${(p) => p.theme.colors.background};
`;
